import { datadogRum } from '@datadog/browser-rum';
import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { useCookies } from 'react-cookie';
import awsRum from '../../awsRum';
import config from '../../config';
import { GetLiveUserInfoQuery, useGetLiveUserInfoQuery } from '../../generated/core-api/graphql-react-query';

export function useUserInfo(token?: string) {
  const [retry, setRetry] = React.useState(true);

  const userInfo = useGetLiveUserInfoQuery<GetLiveUserInfoQuery, Error>({}, {
    retry,
    enabled: !!token,
  });

  React.useEffect(() => {
    if (userInfo.error?.message === 'User not found') {
      setRetry(false);
    } else {
      if (userInfo.error) {
        console.error('Error getting user info', userInfo.error);
      }
      setRetry(true);
    }
  }, [userInfo.error, token]);

  React.useEffect(() => {
    if (userInfo.data) {
      awsRum?.addSessionAttributes({
        userId: userInfo.data.getUserInfo.id,
        email: userInfo.data.getUserInfo.email,
        organisationId: userInfo.data.getUserInfo.organisationId,
        role: userInfo.data.getUserInfo.role,
        isStaff: userInfo.data.getUserInfo.isStaff,
      });
      datadogRum.setUser({
        userId: userInfo.data.getUserInfo.id,
        email: userInfo.data.getUserInfo.email,
        organisationId: userInfo.data.getUserInfo.organisationId,
        role: userInfo.data.getUserInfo.role,
        isStaff: userInfo.data.getUserInfo.isStaff,
      });
    }
  }, [userInfo.data]);
  return userInfo;
}

export function useUserInfoData(): GetLiveUserInfoQuery['getUserInfo'] {
  const userInfo = useUserInfo();
  if (!userInfo.data) {
    throw new Error('User info not loaded');
  }
  return userInfo.data.getUserInfo;
}

function doNothing() { }

interface CookieContextProps {
  removeToken: () => void;
  setToken: (value: string) => void;
  token: string | undefined;
}
const CookieContext = React.createContext<CookieContextProps>({
  removeToken: doNothing,
  setToken: doNothing,
  token: undefined,
});

export function CookieProvider(props: { children: React.ReactNode }) {
  const { children } = props;
  const [cookies, setCookies, removeCookie] = useCookies([
    config.Auth.Cookies.TokenCookieName ?? '',
  ]);
  const token: string | undefined = cookies[config.Auth.Cookies.TokenCookieName ?? ''];

  const queryClient = useQueryClient();

  function setToken(value: string) {
    setCookies(config.Auth.Cookies.TokenCookieName ?? '', value, {
      path: '/',
      sameSite: 'lax',
      domain: config.Auth.Cookies.CookieDomain,
      secure: true,
      maxAge: 3600 * 24 * 365,
    });
  }

  function removeToken() {
    removeCookie(config.Auth.Cookies.TokenCookieName ?? '', { domain: config.Auth.Cookies.CookieDomain });
    removeCookie('token');
  }

  const org: string | undefined = cookies[config.Auth.Cookies.OrgOverrideCookieName ?? ''];
  const role: string | undefined = cookies[config.Auth.Cookies.RoleOverrideCookieName ?? ''];
  React.useEffect(() => {
    queryClient.invalidateQueries();
  }, [token]);

  const value = React.useMemo(() => ({
    setToken, removeToken: () => { removeToken(); }, token, org, role: role ? parseInt(role, 10) : undefined,
  }), [setToken, removeToken, token, org]);

  return (
    <CookieContext.Provider value={value}>
      {children}
    </CookieContext.Provider>
  );
}

export function useCookieContext() { return React.useContext(CookieContext); }
